import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Save, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import FieldEditor from "@/components/FieldEditor";
import { STATUS_BADGE, formatCurrency, formatNumber } from "@/lib/constants";

export default function RecordEditPage() {
  const { moduleKey, recordId } = useParams();
  const { perms } = useAuth();
  const perm = (perms && perms[moduleKey]) || { read: true, create: true, update: true, delete: true };
  const fieldWhitelist = Array.isArray(perm.update) ? new Set(perm.update) : null;
  const canUpdate = perm.update === true || (fieldWhitelist && fieldWhitelist.size > 0);
  const [meta, setMeta] = useState(null);
  const [record, setRecord] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get("/meta/modules"),
      api.get(`/modules/${moduleKey}/records/${recordId}`),
    ])
      .then(([m, r]) => {
        if (!mounted) return;
        setMeta((m.data.modules || []).find((x) => x.key === moduleKey));
        setRecord(r.data);
        setFormData(r.data?.data || {});
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e.response?.data?.detail || "Failed to load record");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [moduleKey, recordId]);

  const isLocked = (key) => !!fieldWhitelist && !fieldWhitelist.has(key);

  // Field Engineer Expense — keep Total in sync with the five expense lines.
  useEffect(() => {
    if (moduleKey !== "engineer_expense") return;
    const t = ["travel_expense", "food_expense", "stay_expense", "local_conveyance", "other_expense"]
      .reduce((s, k) => s + (Number(formData[k]) || 0), 0);
    if ((Number(formData.total) || 0) !== t) setFormData((prev) => ({ ...prev, total: t }));
  }, [moduleKey, formData]);

  const editableFields = useMemo(
    () => (meta?.fields || []).filter((f) => !isLocked(f.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meta, perms]
  );
  const lockedFields = useMemo(
    () => (meta?.fields || []).filter((f) => isLocked(f.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meta, perms]
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/modules/${moduleKey}/records/${recordId}`, { data: formData });
      setRecord((prev) => (prev ? { ...prev, data: { ...formData } } : prev));
      toast.success("Changes saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-sm text-red-700">
            {error}. <Link to={`/m/${moduleKey}`} className="underline font-semibold">Back to list</Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!meta || !record) return null;

  if (!canUpdate) {
    return (
      <div className="p-8">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6 text-sm text-amber-800 flex items-center gap-2">
            <Lock className="h-4 w-4" /> You have read-only access to this record.{" "}
            <Link to={`/m/${moduleKey}/${recordId}`} className="underline font-semibold">View it instead</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const title = record.data?.[meta.title_field] || "Record";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 pb-24" data-testid="record-edit-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{meta.group} · {meta.label}</div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1" data-testid="edit-record-title">Edit · {title}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {fieldWhitelist
              ? `You can edit ${editableFields.length} of ${meta.fields.length} fields. The rest are shown read-only below.`
              : "All fields are editable."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/m/${moduleKey}/${recordId}`}>
            <Button variant="outline" data-testid="back-to-record">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Record
            </Button>
          </Link>
        </div>
      </div>

      {/* Editable fields */}
      <Card className="border-slate-200" data-testid="editable-fields-card">
        <CardContent className="p-5 sm:p-6">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-5">
            Editable fields · {editableFields.length}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {editableFields.map((f) => (
              <div key={f.key} className={f.type === "textarea" || f.type === "attachments" ? "md:col-span-2" : ""}>
                <FieldEditor
                  field={f}
                  value={formData[f.key]}
                  onChange={(v) => setFormData((prev) => ({ ...prev, [f.key]: v }))}
                  disabled={moduleKey === "engineer_expense" && f.key === "total"}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Locked / reference fields */}
      {lockedFields.length > 0 && (
        <Card className="border-slate-200 bg-slate-50/50" data-testid="locked-fields-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-5">
              <Lock className="h-3.5 w-3.5" /> Reference details · read-only · {lockedFields.length} fields
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              {lockedFields.map((f) => (
                <div key={f.key} className={f.type === "textarea" || f.type === "attachments" ? "md:col-span-2" : ""} data-testid={`locked-field-${f.key}`}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{f.label}</div>
                  <div className="text-sm text-slate-700">
                    <LockedValue value={record.data?.[f.key]} field={f} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-white border-t border-slate-200 px-4 sm:px-6 lg:px-8 py-3 flex justify-end gap-2 z-20">
        <Link to={`/m/${moduleKey}/${recordId}`}>
          <Button variant="outline" data-testid="edit-cancel">Cancel</Button>
        </Link>
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="edit-save">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
}

function LockedValue({ value, field }) {
  if (value == null || value === "") return <span className="text-slate-400">—</span>;
  if (field.type === "status") return <Badge variant="outline" className={STATUS_BADGE(value)}>{value}</Badge>;
  if (field.type === "currency") return <span className="font-mono">{formatCurrency(value)}</span>;
  if (field.type === "number") return <span className="font-mono">{formatNumber(value)}</span>;
  if (field.type === "attachments") {
    const n = Array.isArray(value) ? value.length : 0;
    return <span className="text-slate-500">{n ? `${n} file(s) attached` : "—"}</span>;
  }
  return <span className="whitespace-pre-wrap break-words">{String(value)}</span>;
}
